import { useEffect, useState } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { CButton } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilReload } from "@coreui/icons";
import { loginRequest } from "../../config/auth";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { FailureCard } from "../common/FailureCard";
import { trackException } from "../../lib/telemetry";

interface RequireAuthenticationProps {
  children: React.ReactNode;
}

export function RequireAuthentication({ children }: RequireAuthenticationProps) {
  const { instance, inProgress } = useMsal();
  const [redirectError, setRedirectError] = useState<Error | null>(null);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && instance.getAllAccounts().length === 0) {
      // A rejected loginRedirect never navigates, so without this the member waits on the spinner
      // for a redirect that is not coming — indefinitely, and silently.
      instance.loginRedirect(loginRequest).catch((error: unknown) => {
        console.error("Could not start sign-in", error);
        trackException(error, { stage: "sign-in start" });
        setRedirectError(error instanceof Error ? error : new Error(String(error)));
      });
    }
  }, [instance, inProgress]);

  if (redirectError) {
    return (
      <FailureCard
        title="We couldn't take you to sign in"
        message="We could not reach the sign-in service. Check your connection and try again; if it keeps happening, email help@scryerlabs.com."
        details={redirectError.message || null}
      >
        <CButton color="primary" onClick={() => window.location.reload()}>
          <CIcon icon={cilReload} className="me-2" />
          Try again
        </CButton>
      </FailureCard>
    );
  }

  if (inProgress !== InteractionStatus.None) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoadingSpinner />
      </UnauthenticatedTemplate>
    </>
  );
}
